import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../components/AppIcon'; // Utilisation de votre composant Icon personnalisé
import './Rechercher.css';

// Importation des données des pays
import countriesData from '../../country/countriesData.json';

// Créer une instance axios avec timeout
const api = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Données des packs d'alerte
const ALERT_PACKS = [
  { id: 1, name: 'Pack Bronze', duration: '7 jours', price: 500, features: ['1 alerte active', 'Notifications par email'] },
  { id: 2, name: 'Pack Argent', duration: '30 jours', price: 1500, features: ['5 alertes actives', 'Notifications SMS', 'Priorité haute'] },
  { id: 3, name: 'Pack Or', duration: '90 jours', price: 3500, features: ['Alertes illimitées', 'Notifications push', 'Support prioritaire', 'Statistiques détaillées'] },
];

const SearchResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // État pour les paramètres de recherche
  const [searchQuery, setSearchQuery] = useState(queryParams.get('query') || '');
  const [category, setCategory] = useState(queryParams.get('category') || null);
  const [categoryName, setCategoryName] = useState(queryParams.get('categoryName') || null);
  const [subcategoryId, setSubcategoryId] = useState(queryParams.get('subcategoryId') || null);
  const [subcategoryName, setSubcategoryName] = useState(queryParams.get('subcategoryName') || null);
  
  // État pour les résultats
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // État pour la pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // État pour les filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    etat: '',
    country: null,
    city: ''
  });

  // États pour la sélection du pays et ville
  const [countrySearch, setCountrySearch] = useState('');
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  // États pour la localisation
  const [locating, setLocating] = useState(false);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [showLocationAlert, setShowLocationAlert] = useState(false);

  // États pour la mise en alerte
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Charger les résultats au montage
  useEffect(() => {
    if (searchQuery || category || subcategoryId) {
      fetchResults(true);
    }
    
    // Demander la permission de localisation au démarrage (pour le web)
    if ('geolocation' in navigator) {
      setLocationPermissionGranted(true);
    }
  }, []);

  // Filtrer les pays pour la recherche
  useEffect(() => {
    if (countrySearch.trim() === '') {
      setFilteredCountries(countriesData.slice(0, 195));
    } else {
      const searchLower = countrySearch.toLowerCase();
      const filtered = countriesData.filter(country =>
        country.name.toLowerCase().includes(searchLower) ||
        country.nativeName?.toLowerCase().includes(searchLower) ||
        country.capital?.toLowerCase().includes(searchLower) ||
        country.code.toLowerCase().includes(searchLower)
      ).slice(0, 15);
      setFilteredCountries(filtered);
    }
  }, [countrySearch]);

  // Filtrer les villes pour le pays sélectionné
  useEffect(() => {
    if (filters.country) {
      const countryCities = filters.country.cities || [];
      setFilteredCities(countryCities);
    } else {
      setFilteredCities([]);
    }
  }, [filters.country]);

  // Fonction pour obtenir la localisation actuelle (version web)
  const getCurrentLocation = async () => {
    setLocating(true);
    
    try {
      if (!('geolocation' in navigator)) {
        alert('La géolocalisation n\'est pas supportée par votre navigateur');
        setLocating(false);
        return;
      }

      console.log('Début de la géolocalisation...');
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('Position obtenue:', latitude, longitude);
          reverseGeocode(latitude, longitude);
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
          setLocating(false);
          
          let errorMessage = 'Impossible d\'obtenir votre localisation';
          switch (error.code) {
            case 1:
              errorMessage = 'Permission de localisation refusée';
              break;
            case 2:
              errorMessage = 'Position indisponible';
              break;
            case 3:
              errorMessage = 'Temps d\'attente dépassé';
              break;
          }
          
          alert(`Erreur de localisation: ${errorMessage}`);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 15000, 
          maximumAge: 10000 
        }
      );
    } catch (error) {
      console.error('Erreur lors de la localisation:', error);
      setLocating(false);
      alert('Erreur: Une erreur est survenue lors de la localisation');
    }
  };

  // Traitement automatique de la localisation
  const handleAutoLocation = () => {
    setShowLocationAlert(true);
    setTimeout(() => {
      getCurrentLocation();
    }, 100);
  };

  // Géocodage inversé pour obtenir la ville et le pays
  const reverseGeocode = async (latitude, longitude) => {
    try {
      console.log('Début du géocodage inversé...');
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'AutoCassesApp/1.0'
          }
        }
      );

      const data = response.data;
      const address = data.address;
      
      let city = '';
      let countryName = '';
      
      if (address.city) {
        city = address.city;
      } else if (address.town) {
        city = address.town;
      } else if (address.village) {
        city = address.village;
      } else if (address.municipality) {
        city = address.municipality;
      }
      
      if (address.country) {
        countryName = address.country;
      }
      
      console.log('Adresse trouvée:', { city, countryName, fullAddress: address });
      
      const foundCountry = countriesData.find(country => 
        country.name.toLowerCase().includes(countryName.toLowerCase()) ||
        country.nativeName?.toLowerCase().includes(countryName.toLowerCase()) ||
        (address.country_code && country.code.toLowerCase() === address.country_code.toLowerCase())
      );
      
      if (foundCountry) {
        setFilters(prev => ({
          ...prev,
          country: foundCountry,
          city: city
        }));
        
        // Appliquer automatiquement les filtres après 1 seconde
        setTimeout(() => {
          setShowFilters(false);
          fetchResults(true);
        }, 1000);
        
        alert(`✅ Localisation réussie\nPosition détectée: ${city ? city + ', ' : ''}${foundCountry.name}\n\nRecherche en cours...`);
      } else {
        const confirmUse = confirm(
          `ℹ️ Localisation partielle\nPosition trouvée${city ? ' à ' + city : ''}${countryName ? ' en ' + countryName : ''}\n\nVoulez-vous utiliser cette localisation?`
        );
        
        if (confirmUse) {
          const tempCountry = {
            name: countryName || 'Localisation',
            code: 'custom',
            flag: '📍',
            cities: city ? [city] : []
          };
          
          setFilters(prev => ({
            ...prev,
            country: tempCountry,
            city: city
          }));
          
          setTimeout(() => {
            setShowFilters(false);
            fetchResults(true);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Erreur de géocodage inversé:', error);
      alert('❌ Géocodage échoué\nImpossible de déterminer votre ville et pays. Vérifiez votre connexion internet.');
    } finally {
      setLocating(false);
      setShowLocationAlert(false);
    }
  };

  // Fonction pour rechercher les produits
  const fetchResults = async (reset = false) => {
    try {
      if (reset) {
        setPage(1);
        setLoading(true);
      } else {
        if (!hasMore || loading) return;
      }
      
      setError(null);
      
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('q', searchQuery);
      if (category) params.append('categorieId', category);
      
      if (filters.minPrice) params.append('minPrice', filters.minPrice);
      if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
      if (filters.etat) params.append('etat', filters.etat);
      
      if (filters.country) {
        params.append('country', filters.country.name);
      }
      
      if (filters.city) {
        params.append('city', filters.city);
      }
      
      params.append('page', reset ? 1 : page);
      params.append('limit', 20);
      
      let url = '';
      
      if (subcategoryId) {
        url = `https://api.autocasses.com/api/produit/sous-categorie/${subcategoryId}?${params.toString()}`;
      } else if (category || searchQuery) {
        url = `https://api.autocasses.com/api/produit/search?${params.toString()}`;
      } else {
        url = `https://api.autocasses.com/api/produit/search?${params.toString()}`;
      }
      
      console.log('Fetching URL:', url);
      
      const response = await api.get(url);
      const data = response.data;
      
      if (reset) {
        setProduits(data.produits || []);
      } else {
        setProduits(prev => [...prev, ...(data.produits || [])]);
      }
      
      setTotalResults(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setHasMore(data.page < data.totalPages);
      
      if (!reset) {
        setPage(prev => prev + 1);
      }
      
    } catch (error) {
      console.error('Erreur recherche:', error);
      
      let errorMessage = 'Impossible de charger les résultats de recherche';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Temps d\'attente dépassé. Vérifiez votre connexion.';
      } else if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
        errorMessage = `Erreur serveur (${error.response.status})`;
      } else if (error.request) {
        errorMessage = 'Pas de connexion au serveur. Vérifiez votre réseau.';
      }
      
      setError(errorMessage);
      alert(`Erreur: ${errorMessage}`);
      
      if (reset) {
        setProduits([]);
        setTotalResults(0);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Formater le prix des alertes
  const formatAlertPrice = (price) => {
    return `${price} FCFA`;
  };

  // Valider le numéro de téléphone
  const validatePhoneNumber = (number, operator) => {
    const cleaned = number.replace(/\s/g, '');
    
    if (operator === 'orange') {
      const orangeRegex = /^(6|7|9)[0-9]{8}$/;
      return orangeRegex.test(cleaned);
    } else if (operator === 'mtn') {
      const mtnRegex = /^(6|7|9)[0-9]{8}$/;
      return mtnRegex.test(cleaned);
    }
    
    return false;
  };

  // Simuler le paiement
  const handlePayment = async () => {
    setPaymentLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (selectedPaymentMethod === 'orange' || selectedPaymentMethod === 'mtn') {
        if (!validatePhoneNumber(phoneNumber, selectedPaymentMethod)) {
          alert('Erreur', `Numéro ${selectedPaymentMethod === 'orange' ? 'Orange Money' : 'MTN Money'} invalide`);
          setPaymentLoading(false);
          return;
        }
      }
      
      setShowPhoneNumberModal(false);
      setShowPaymentModal(false);
      
      setTimeout(() => {
        setShowSuccessModal(true);
        
        const alertData = {
          query: searchQuery,
          categoryId: category,
          subcategoryId: subcategoryId,
          filters: filters,
          packId: selectedPack.id,
          paymentMethod: selectedPaymentMethod,
          phoneNumber: selectedPaymentMethod === 'orange' || selectedPaymentMethod === 'mtn' ? phoneNumber : null,
          createdAt: new Date().toISOString()
        };
        
        console.log('Alerte créée:', alertData);
        
        setTimeout(() => {
          setShowSuccessModal(false);
          setSelectedPack(null);
          setSelectedPaymentMethod(null);
          setPhoneNumber('');
        }, 3000);
      }, 500);
      
    } catch (error) {
      alert('Erreur', 'Le paiement a échoué. Veuillez réessayer.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Rafraîchir les résultats
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchResults(true);
  }, [searchQuery, category, subcategoryId, filters]);

  // Charger plus de résultats
  const loadMore = () => {
    if (!loading && hasMore) {
      fetchResults(false);
    }
  };

  // Appliquer les filtres
  const applyFilters = () => {
    setShowFilters(false);
    fetchResults(true);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      etat: '',
      country: null,
      city: ''
    });
    setCountrySearch('');
    fetchResults(true);
  };

  // Sélectionner un pays
  const handleSelectCountry = (country) => {
    setFilters(prev => ({ 
      ...prev, 
      country: country,
      city: ''
    }));
    setCountrySearch('');
  };

  // Sélectionner une ville
  const handleSelectCity = (city) => {
    setFilters(prev => ({ ...prev, city }));
  };

  // Formater le prix des produits
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Naviguer vers les détails du produit
  const navigateToProduct = (produit) => {
    navigate(`/product/${produit.id}`, { state: { product: produit } });
  };

  // Rendu d'un produit dans la grille
  const renderProductItem = (item, index) => (
    <div
      key={item.id}
      className="product-card"
      style={{ marginLeft: index % 2 === 0 ? 0 : '8px' }}
      onClick={() => navigateToProduct(item)}
    >
      <div className="product-image-container">
        {item.photos && item.photos.length > 0 ? (
          <img
            src={item.photos[0].photo_url}
            alt={item.titre}
            className="product-image"
          />
        ) : (
          <div className="product-image no-image">
            <Icon name="Image" size={30} color="#94a3b8" />
          </div>
        )}
        
        {item.etat && (
          <div className={`etat-badge ${item.etat.toLowerCase().replace(' ', '-')}`}>
            <span className="etat-text">{item.etat}</span>
          </div>
        )}
        
        {item.negociable === 'true' && (
          <div className="negociable-badge">
            <Icon name="DollarSign" size={10} color="#fff" />
            <span className="negociable-text">Nég.</span>
          </div>
        )}
      </div>
      
      <div className="product-info">
        <h3 className="product-title" title={item.titre}>
          {item.titre}
        </h3>
        
        <p className="product-price">
          {formatPrice(item.prix)}
        </p>
        
        <div className="product-meta">
          <div className="location-container">
            <Icon name="MapPin" size={12} color="#64748b" />
            <span className="product-location" title={`${item.city || 'Ville'}, ${item.country || 'Pays'}`}>
              {item.city || 'Ville'}, {item.country || 'Pays'}
            </span>
          </div>
          
          <span className="product-date">
            {new Date(item.createdAt).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'short'
            })}
          </span>
        </div>
      </div>
    </div>
  );

  // Rendu du footer
  const renderFooter = () => {
    if (!loading && !hasMore && produits.length > 0) {
      return (
        <div className="footer">
          <span className="footer-text">🎯 Fin des résultats</span>
        </div>
      );
    }
    
    if (loading && page > 1) {
      return (
        <div className="footer">
          <div className="loader"></div>
          <span className="footer-text">Chargement...</span>
        </div>
      );
    }
    
    return null;
  };

  // Rendu vide
  const renderEmpty = () => {
    if (loading) return null;
    
    return (
      <div className="empty-container">
        <div className="empty-illustration">
          <Icon name="Search" size={80} color="#cbd5e1" />
        </div>
        <h2 className="empty-title">Aucun résultat trouvé</h2>
        <p className="empty-text">
          {searchQuery 
            ? `Aucun produit correspondant à "${searchQuery}"`
            : subcategoryName
            ? `Aucun produit dans "${subcategoryName}"`
            : categoryName
            ? `Aucun produit dans "${categoryName}"`
            : 'Aucun produit disponible'
          }
        </p>
        
        <div className="empty-actions">
          <button
            className="empty-button modify-button"
            onClick={() => navigate(-1)}
          >
            <Icon name="Search" size={18} color="#fff" />
            <span className="empty-button-text">Modifier la recherche</span>
          </button>
          
          <button
            className="empty-button alert-button"
            onClick={() => setShowAlertModal(true)}
          >
            <Icon name="Bell" size={18} color="#fff" />
            <span className="empty-button-text">Mettre en alerte</span>
          </button>
        </div>
        
        <p className="alert-info">
          🔔 Activez une alerte pour être notifié dès qu'un produit correspondant est disponible.
        </p>
      </div>
    );
  };

  // Gérer le scroll infini
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      loadMore();
    }
  };

  return (
    <div className="rechercher-container">
      {/* En-tête amélioré */}
      <header className="header">
        <button 
          className="back-button"
          onClick={() => navigate(-1)}
        >
          <Icon name="ArrowLeft" size={22} color="#1e293b" />
        </button>
        
        <div className="search-container">
          <div className="search-bar">
            <Icon name="Search" size={20} color="#64748b" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchResults(true)}
            />
            {searchQuery.length > 0 && (
              <button 
                className="clear-button"
                onClick={() => setSearchQuery('')}
              >
                <Icon name="X" size={18} color="#94a3b8" />
              </button>
            )}
          </div>
          
          <button 
            className={`filter-button ${Object.values(filters).some(f => f) ? 'active' : ''}`}
            onClick={() => setShowFilters(true)}
          >
            <Icon 
              name="Filter" 
              size={22} 
              color={Object.values(filters).some(f => f) ? '#fff' : '#3b82f6'} 
            />
            {Object.values(filters).some(f => f) && (
              <span className="filter-badge">
                {Object.values(filters).filter(f => f).length}
              </span>
            )}
          </button>
        </div>
      </header>
      
      {/* Barre d'information */}
      <div className="info-bar">
        <div className="info-left">
          <h1 className="search-title" title={subcategoryName || categoryName || searchQuery || 'Tous les produits'}>
            {subcategoryName || categoryName || searchQuery || 'Tous les produits'}
          </h1>
          {totalResults > 0 && (
            <p className="results-count">
              {totalResults} {totalResults === 1 ? 'produit' : 'produits'}
            </p>
          )}
        </div>
        
        {produits.length > 0 && (
          <button 
            className="sort-button"
            onClick={() => setShowFilters(true)}
          >
            <Icon name="SlidersHorizontal" size={18} color="#3b82f6" />
            <span className="sort-text">Filtrer</span>
          </button>
        )}
      </div>
      
      {/* Filtres actifs */}
      {(filters.country || filters.city || filters.etat || filters.minPrice || filters.maxPrice) && (
        <div className="active-filters-container">
          <div className="active-filters-scroll">
            {filters.country && (
              <div className="active-filter-tag">
                <Icon name="Flag" size={12} color="#fff" />
                <span className="active-filter-text">{filters.country.name}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, country: null, city: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.city && (
              <div className="active-filter-tag">
                <Icon name="Building" size={12} color="#fff" />
                <span className="active-filter-text">{filters.city}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, city: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.etat && (
              <div className="active-filter-tag">
                <Icon name="CheckCircle" size={12} color="#fff" />
                <span className="active-filter-text">{filters.etat}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, etat: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.minPrice && (
              <div className="active-filter-tag">
                <Icon name="TrendingUp" size={12} color="#fff" />
                <span className="active-filter-text">Min: {filters.minPrice}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, minPrice: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.maxPrice && (
              <div className="active-filter-tag">
                <Icon name="TrendingDown" size={12} color="#fff" />
                <span className="active-filter-text">Max: {filters.maxPrice}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, maxPrice: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            <button 
              className="clear-all-button"
              onClick={resetFilters}
            >
              <span className="clear-all-text">Tout effacer</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Grille des résultats */}
      <div className="grid-container" onScroll={handleScroll}>
        <div className="products-grid">
          {produits.map((item, index) => renderProductItem(item, index))}
        </div>
        {renderFooter()}
        {produits.length === 0 && !loading && renderEmpty()}
      </div>
      
      {/* Bouton flottant de recherche */}
      {(searchQuery || category || subcategoryId) && produits.length > 0 && (
        <button
          className="floating-button"
          onClick={() => fetchResults(true)}
        >
          <div className="floating-button-content">
            <Icon name="Search" size={22} color="#fff" />
            <span className="floating-button-text">
              {searchQuery 
                ? `"${searchQuery}"`
                : subcategoryName || categoryName
              }
            </span>
          </div>
        </button>
      )}
      
      {/* Modal des filtres améliorée */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <button 
                  className="modal-close-button"
                  onClick={() => setShowFilters(false)}
                >
                  <Icon name="X" size={24} color="#64748b" />
                </button>
                <h2 className="modal-title">Filtres</h2>
              </div>
              
              <button 
                className="modal-reset-button"
                onClick={resetFilters}
              >
                <span className="modal-reset-text">Réinitialiser</span>
              </button>
            </div>
            
            <div className="filters-container">
              {/* Section Localisation - Bouton Me localiser */}
              <div className="filter-section">
                <h3 className="section-title">📍 Localisation</h3>
                
                <button
                  className={`location-button ${locating ? 'locating' : ''}`}
                  onClick={handleAutoLocation}
                  disabled={locating}
                >
                  <div className="location-button-icon">
                    {locating ? (
                      <div className="loader"></div>
                    ) : (
                      <Icon name="Navigation" size={22} color="#fff" />
                    )}
                  </div>
                  <div className="location-button-text-container">
                    <span className="location-button-title">
                      {locating ? 'Localisation en cours...' : 'Me localiser'}
                    </span>
                    <span className="location-button-subtitle">
                      Utiliser ma position actuelle
                    </span>
                  </div>
                  <Icon name="ChevronRight" size={20} color="#fff" />
                </button>
                
                {showLocationAlert && (
                  <div className="location-alert">
                    <div className="loader"></div>
                    <span className="location-alert-text">
                      Recherche de votre position...
                    </span>
                  </div>
                )}
              </div>
              
              {/* Section Prix */}
              <div className="filter-section">
                <h3 className="section-title">💰 Prix</h3>
                <div className="price-row">
                  <div className="price-input-container">
                    <label className="price-label">Min (FCFA)</label>
                    <input
                      type="number"
                      className="price-input"
                      placeholder="0"
                      value={filters.minPrice}
                      onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                    />
                  </div>
                  <div className="price-separator">
                    <span className="price-separator-text">à</span>
                  </div>
                  <div className="price-input-container">
                    <label className="price-label">Max (FCFA)</label>
                    <input
                      type="number"
                      className="price-input"
                      placeholder="Illimité"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Section État */}
              <div className="filter-section">
                <h3 className="sectionTitle">⚡ État</h3>
                <div className="chip-container">
                  {['', 'Neuf', 'Bon État', 'État Correct', 'Mauvais État','Comme neuf', 'Pour Pièces Uniquement'].map((etat) => (
                    <button
                      key={etat}
                      className={`chip ${filters.etat === etat ? 'selected' : ''}`}
                      onClick={() => setFilters(prev => ({ ...prev, etat }))}
                    >
                      <span className={`chip-text ${filters.etat === etat ? 'selected' : ''}`}>
                        {etat || 'Tous états'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Section Pays */}
              <div className="filter-section" style={{marginBottom: '30px'}}>
                <h3 className="sectionTitle">🌍 Pays</h3>
                <div className="country-search-container">
                  <Icon name="Search" size={18} color="#64748b" />
                  <input
                    type="text"
                    className="country-search-input"
                    placeholder="Rechercher un pays..."
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                  />
                </div>
                
                <div className="country-list">
                  {filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      className={`country-item ${filters.country?.code === country.code ? 'selected' : ''}`}
                      onClick={() => handleSelectCountry(country)}
                    >
                      <span className="country-flag">{country.flag}</span>
                      <div className="country-info">
                        <span className="country-name">{country.name}</span>
                        <span className="country-code">{country.code}</span>
                      </div>
                      {filters.country?.code === country.code && (
                        <Icon name="CheckCircle" size={20} color="#10b981" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Section Ville (si pays sélectionné) */}
              {filters.country && filteredCities.length > 0 && (
                <div className="filter-section" style={{marginBottom: '150px'}}>
                  <h3 className="sectionTitle">🏙️ Ville</h3>
                  <div className="city-list">
                    {filteredCities.map((city, index) => (
                      <button
                        key={index}
                        className={`city-item ${filters.city === city ? 'selected' : ''}`}
                        onClick={() => handleSelectCity(city)}
                      >
                        <Icon 
                          name="Building" 
                          size={18} 
                          color={filters.city === city ? '#3b82f6' : '#64748b'} 
                        />
                        <span className={`city-text ${filters.city === city ? 'selected' : ''}`}>
                          {city}
                        </span>
                        {filters.city === city && (
                          <Icon name="CheckCircle" size={18} color="#10b981" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Bouton Appliquer */}
            <div className="modal-footer">
              <button
                className="apply-button"
                onClick={applyFilters}
              >
                <span className="apply-button-text">
                  Appliquer les filtres
                  {totalResults > 0 && ` (${totalResults})`}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal des packs d'alerte */}
      {showAlertModal && (
        <div className="modal-overlay" onClick={() => setShowAlertModal(false)}>
          <div className="modal-content alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button 
                className="modal-close-button"
                onClick={() => setShowAlertModal(false)}
              >
                <Icon name="X" size={24} color="#64748b" />
              </button>
              <h2 className="modal-title">Choisissez un pack d'alerte</h2>
            </div>
            
            <div className="alert-packs-container">
              <p className="alert-modal-description">
                Soyez notifié dès qu'un produit correspondant à votre recherche est disponible
              </p>
              
              {ALERT_PACKS.map((pack) => (
                <div
                  key={pack.id}
                  className={`alert-pack-card ${selectedPack?.id === pack.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedPack(pack);
                    setTimeout(() => {
                      setShowAlertModal(false);
                      setShowPaymentModal(true);
                    }, 300);
                  }}
                >
                  <div className="alert-pack-header">
                    <h3 className="alert-pack-name">{pack.name}</h3>
                    <span className="alert-pack-duration">{pack.duration}</span>
                  </div>
                  
                  <p className="alert-pack-price">
                    {formatAlertPrice(pack.price)}
                  </p>
                  
                  <div className="alert-pack-features">
                    {pack.features.map((feature, index) => (
                      <div key={index} className="alert-pack-feature">
                        <Icon name="CheckCircle" size={16} color="#10b981" />
                        <span className="alert-pack-feature-text">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    className="alert-pack-select-button"
                    onClick={() => {
                      setSelectedPack(pack);
                      setTimeout(() => {
                        setShowAlertModal(false);
                        setShowPaymentModal(true);
                      }, 300);
                    }}
                  >
                    <span className="alert-pack-select-button-text">
                      Sélectionner
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de paiement */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => {
          setShowPaymentModal(false);
          setSelectedPaymentMethod(null);
        }}>
          <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button 
                className="modal-close-button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPaymentMethod(null);
                }}
              >
                <Icon name="ArrowLeft" size={24} color="#64748b" />
              </button>
              <h2 className="modal-title">Méthode de paiement</h2>
            </div>
            
            {selectedPack && (
              <div className="payment-summary">
                <p className="payment-summary-title">Pack {selectedPack.name}</p>
                <p className="payment-summary-price">{formatAlertPrice(selectedPack.price)}</p>
              </div>
            )}
            
            <div className="payment-methods-container">
              <button
                className={`payment-method-card ${selectedPaymentMethod === 'orange' ? 'selected' : ''}`}
                onClick={() => setSelectedPaymentMethod('orange')}
              >
                <div className="payment-method-icon">
                  <span className="payment-method-icon-text">🟠</span>
                </div>
                <div className="payment-method-info">
                  <h4 className="payment-method-name">Orange Money</h4>
                  <p className="payment-method-description">Paiement sécurisé via votre compte Orange Money</p>
                </div>
                {selectedPaymentMethod === 'orange' && (
                  <Icon name="CheckCircle" size={24} color="#10b981" />
                )}
              </button>
              
              <button
                className={`payment-method-card ${selectedPaymentMethod === 'mtn' ? 'selected' : ''}`}
                onClick={() => setSelectedPaymentMethod('mtn')}
              >
                <div className="payment-method-icon">
                  <span className="payment-method-icon-text">🟡</span>
                </div>
                <div className="payment-method-info">
                  <h4 className="payment-method-name">MTN Money</h4>
                  <p className="payment-method-description">Paiement sécurisé via votre compte MTN Money</p>
                </div>
                {selectedPaymentMethod === 'mtn' && (
                  <Icon name="CheckCircle" size={24} color="#10b981" />
                )}
              </button>
              
              <button
                className={`payment-method-card ${selectedPaymentMethod === 'inapp' ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedPaymentMethod('inapp');
                  handlePayment();
                }}
              >
                <div className="payment-method-icon">
                  <Icon name="ShoppingCart" size={24} color="#3b82f6" />
                </div>
                <div className="payment-method-info">
                  <h4 className="payment-method-name">Achats intégrés</h4>
                  <p className="payment-method-description">Paiement via carte bancaire</p>
                </div>
                {selectedPaymentMethod === 'inapp' && (
                  <Icon name="CheckCircle" size={24} color="#10b981" />
                )}
              </button>
            </div>
            
            {(selectedPaymentMethod === 'orange' || selectedPaymentMethod === 'mtn') && (
              <button
                className="payment-proceed-button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowPhoneNumberModal(true);
                }}
                disabled={paymentLoading}
              >
                <span className="payment-proceed-button-text">
                  {paymentLoading ? 'Chargement...' : 'Continuer'}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Modal pour numéro de téléphone (Orange/MTN Money) */}
      {showPhoneNumberModal && (
        <div className="modal-overlay" onClick={() => {
          setShowPhoneNumberModal(false);
          setPhoneNumber('');
        }}>
          <div className="modal-content phone-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button 
                className="modal-close-button"
                onClick={() => {
                  setShowPhoneNumberModal(false);
                  setPhoneNumber('');
                  setShowPaymentModal(true);
                }}
              >
                <Icon name="ArrowLeft" size={24} color="#64748b" />
              </button>
              <h2 className="modal-title">
                {selectedPaymentMethod === 'orange' ? 'Orange Money' : 'MTN Money'}
              </h2>
            </div>
            
            <div className="phone-modal-body">
              <p className="phone-modal-description">
                Entrez votre numéro de téléphone pour procéder au paiement de {selectedPack ? formatAlertPrice(selectedPack.price) : ''}
              </p>
              
              <div className="phone-input-container">
                <span className="phone-prefix">+237</span>
                <input
                  type="tel"
                  className="phone-input"
                  placeholder="6XX XXX XXX"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  maxLength={9}
                />
              </div>
              
              <p className="phone-validation-info">
                {selectedPaymentMethod === 'orange' 
                  ? 'Format: 6XX XXX XXX, 7XX XXX XXX ou 9XX XXX XXX'
                  : 'Format: 6XX XXX XXX, 7XX XXX XXX ou 9XX XXX XXX'
                }
              </p>
            </div>
            
            <div className="modal-footer">
              <button
                className={`phone-submit-button ${(!phoneNumber || phoneNumber.length < 9) ? 'disabled' : ''}`}
                onClick={handlePayment}
                disabled={!phoneNumber || phoneNumber.length < 9 || paymentLoading}
              >
                {paymentLoading ? (
                  <div className="loader"></div>
                ) : (
                  <>
                    <span className="phone-submit-button-text">Payer maintenant</span>
                    {selectedPack && (
                      <span className="phone-submit-button-amount">
                        {formatAlertPrice(selectedPack.price)}
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de succès */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon-container">
              <Icon name="CheckCircle" size={80} color="#10b981" />
            </div>
            
            <h2 className="success-title">Paiement effectué avec succès !</h2>
            
            <p className="success-message">
              Votre alerte a été activée. Vous serez notifié dès qu'un produit correspondant à votre recherche sera disponible.
            </p>
            
            <div className="success-details">
              <div className="success-detail-row">
                <span className="success-detail-label">Pack:</span>
                <span className="success-detail-value">{selectedPack?.name}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Durée:</span>
                <span className="success-detail-value">{selectedPack?.duration}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Montant:</span>
                <span className="success-detail-value">{selectedPack ? formatAlertPrice(selectedPack.price) : ''}</span>
              </div>
            </div>
            
            <button
              className="success-button"
              onClick={() => setShowSuccessModal(false)}
            >
              <span className="success-button-text">Fermer</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Indicateur de chargement */}
      {loading && page === 1 && (
        <div className="loading-container">
          <div className="loader large"></div>
          <p className="loading-text">Recherche en cours...</p>
        </div>
      )}
    </div>
  );
};

export default SearchResults;